/**
 * TanStack Query hooks for the Resource Plan M4 API — versions, compare, approvals, export.
 *
 * Same conventions as use-resource-plans.ts and use-plan-composition.ts: every mutation
 * owns its own toast, writes no optimistic cache entries, and invalidates rather than
 * patching (.claude/rules/frontend.md).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY THESE INVALIDATE TWO KEY FAMILIES AND THE M3 HOOKS INVALIDATE ONE
 * ──────────────────────────────────────────────────────────────────────────────
 * An M3 mutation returns the whole re-shaped plan, so `resourcePlanKeys.all` is enough. An
 * M4 governance action changes something that is NOT part of the plan detail — a version
 * list, an approval decision — while ALSO moving the three counts the plan header reads
 * (`versionNumber`, `pendingApprovalId`, `frozenVersionCount`). Invalidating only one of
 * the two families leaves the header saying "Awaiting Approval" after the approval has been
 * granted, or a Versions tab with a stale count.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE EXPORT IS NOT A QUERY
 * ──────────────────────────────────────────────────────────────────────────────
 * It returns bytes, not JSON, and its result is a file the browser saves. So it is a
 * `useMutation` that resolves to a Blob and triggers the download itself — caching a
 * spreadsheet under a query key would serve a stale document the next time the menu opened,
 * and a plan changes on every keystroke.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PlanCompareView } from "@/lib/plan-compare";
import type { PlanExportVariant } from "@/lib/plan-export/model";
import {
  planApprovalKeys,
  planCompareKeys,
  planExportKeys,
  planVersionKeys,
  resourcePlanKeys,
} from "@/lib/query";
import type {
  PlanApprovalDetailView,
  PlanApprovalQueueEntryView,
  PlanApprovalSummaryView,
  PlanVersionDetailView,
  PlanVersionListItemView,
  ResourcePlanDetailView,
} from "@/schemas/resource-plans";

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

async function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  return readJsonOrThrow<T>(
    await fetch(url, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

/** Both families: the governance resource AND the plan header's counts. See the header. */
function useInvalidateGovernance() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: resourcePlanKeys.all });
    qc.invalidateQueries({ queryKey: planVersionKeys.all });
    qc.invalidateQueries({ queryKey: planApprovalKeys.all });
    qc.invalidateQueries({ queryKey: planCompareKeys.all });
    qc.invalidateQueries({ queryKey: planExportKeys.all });
    // The Solutions Dashboard shows Plan Status, and the Account page shows its plans.
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
  };
}

// ── Epic 8 — versions and compare ─────────────────────────────────────────────

export function usePlanVersions(planId: string, enabled = true) {
  return useQuery({
    queryKey: planVersionKeys.forPlan(planId),
    queryFn: async () => {
      const { versions } = await send<{ versions: PlanVersionListItemView[] }>(
        `/api/plans/${planId}/versions`,
        "GET",
      );
      return versions;
    },
    enabled,
  });
}

export function usePlanVersion(versionId: string | null) {
  return useQuery({
    queryKey: planVersionKeys.detail(versionId ?? ""),
    queryFn: () => send<PlanVersionDetailView>(`/api/plan-versions/${versionId}`, "GET"),
    enabled: versionId !== null,
  });
}

/** RP-8.4's "compare vs the Won version is one click" — null when there is nothing to compare. */
export function useChangeOrderCompareRefs(planId: string, enabled = true) {
  return useQuery({
    queryKey: planVersionKeys.changeOrderRefs(planId),
    queryFn: async () => {
      const { refs } = await send<{
        refs: { left: string; right: string; sourcePlanName: string } | null;
      }>(`/api/plans/${planId}/change-order-compare`, "GET");
      return refs;
    },
    enabled,
  });
}

export function usePlanComparison(left: string | null, right: string | null) {
  return useQuery({
    queryKey: planCompareKeys.pair(left ?? "", right ?? ""),
    queryFn: () =>
      send<PlanCompareView & { summary: string }>(
        `/api/plan-compare?left=${encodeURIComponent(left ?? "")}&right=${encodeURIComponent(right ?? "")}`,
        "GET",
      ),
    enabled: left !== null && right !== null && left !== right,
  });
}

export type ClonePlanInput =
  | {
      target: "plan";
      name?: string;
      kind?: string;
      startMonth?: string;
      changeOrderReference?: string | null;
      fromVersionId?: string | null;
    }
  | { target: "version"; versionId: string };

/**
 * RP-8.2. Returns the plan the caller should now be looking at: the NEW plan on a clone,
 * and this plan on a restore — so the caller can navigate on the first and stay on the
 * second without asking which happened.
 */
export function useClonePlan(planId: string) {
  const invalidate = useInvalidateGovernance();
  return useMutation({
    mutationFn: (input: ClonePlanInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/clone`, "POST", input),
    onSuccess: (plan, input) => {
      invalidate();
      toast.success(
        input.target === "plan"
          ? `Created "${plan.name}".`
          : "Version restored — it is the current version again.",
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** "New version" — save the plan as a version, in any status but Locked. An empty label is sent as none. */
export function useCommitPlanVersion(planId: string) {
  const invalidate = useInvalidateGovernance();
  return useMutation({
    mutationFn: (input: { label: string | null }) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/versions`, "POST", input),
    onSuccess: (plan) => {
      invalidate();
      toast.success(`Saved version ${plan.versionNumber ?? "?"}.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Delete a version the reader committed themselves. Never the live one. */
export function useDeletePlanVersion() {
  const invalidate = useInvalidateGovernance();
  return useMutation({
    mutationFn: (version: PlanVersionListItemView) =>
      send<ResourcePlanDetailView>(`/api/plan-versions/${version.id}`, "DELETE"),
    onSuccess: (_plan, version) => {
      invalidate();
      toast.success(`Deleted ${version.label}.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Epic 17 — approvals ───────────────────────────────────────────────────────

/** RP-17.3's panel. Rides on `viewResourcePlan`, so the planner sees their own history. */
export function usePlanApprovals(planId: string, enabled = true) {
  return useQuery({
    queryKey: planApprovalKeys.forPlan(planId),
    queryFn: async () => {
      const { approvals } = await send<{ approvals: PlanApprovalSummaryView[] }>(
        `/api/plans/${planId}/approvals`,
        "GET",
      );
      return approvals;
    },
    enabled,
  });
}

/**
 * RP-17.1's exhibit. Gated on `viewPlanCost` server-side, so this 403s for `[ACCT]` — the
 * panel checks `plan.canViewCost` before enabling it rather than showing an error the
 * planner can do nothing about.
 */
export function usePlanApproval(approvalId: string | null, enabled = true) {
  return useQuery({
    queryKey: planApprovalKeys.detail(approvalId ?? ""),
    queryFn: () => send<PlanApprovalDetailView>(`/api/plan-approvals/${approvalId}`, "GET"),
    enabled: approvalId !== null && enabled,
  });
}

export function useApprovalQueue(enabled = true) {
  return useQuery({
    queryKey: planApprovalKeys.queue(),
    queryFn: async () => {
      const { approvals } = await send<{ approvals: PlanApprovalQueueEntryView[] }>(
        "/api/plan-approvals/queue",
        "GET",
      );
      return approvals;
    },
    enabled,
  });
}

export function useDecidePlanApproval(approvalId: string) {
  const invalidate = useInvalidateGovernance();
  return useMutation({
    mutationFn: (input: { decision: "Approved" | "Rejected"; reason?: string | null }) =>
      send<PlanApprovalSummaryView>(`/api/plan-approvals/${approvalId}`, "POST", input),
    onSuccess: (approval) => {
      invalidate();
      toast.success(
        approval.status === "Approved"
          ? "Approved — the plan is now Proposed and the version is frozen."
          : "Rejected. The plan stays Open and the planner can see your reason.",
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Epic 10 — export ──────────────────────────────────────────────────────────

export function usePlanExportOptions(planId: string, enabled = true) {
  return useQuery({
    queryKey: planExportKeys.options(planId),
    queryFn: () =>
      send<{
        versions: Array<{ id: string; label: string; frozenAt: string }>;
        pendingApprovalCount: number;
      }>(`/api/plans/${planId}/export-options`, "GET"),
    enabled,
  });
}

export interface PlanExportRequest {
  variant: PlanExportVariant;
  format: "xlsx" | "pdf";
  versionId?: string | null;
}

/**
 * Download an export (RP-10.1, RP-10.2).
 *
 * The filename comes from `Content-Disposition`, not from anything computed here: the
 * server's model already sanitised it for three operating systems and reconstructing it in
 * the browser would be a second implementation of the same rule.
 */
export function useExportPlan(planId: string) {
  return useMutation({
    mutationFn: async (input: PlanExportRequest) => {
      const params = new URLSearchParams({ variant: input.variant, format: input.format });
      if (input.versionId != null) params.set("versionId", input.versionId);
      const res = await fetch(`/api/plans/${planId}/export?${params.toString()}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Export failed (${res.status})`);
      }
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = /filename="([^"]+)"/.exec(disposition);
      return { blob: await res.blob(), filename: match?.[1] ?? `plan.${input.format}` };
    },
    onSuccess: ({ blob, filename }) => {
      // The browser has no API for "save this response as a file" — an object URL on a
      // synthetic anchor is the only route, and it must be revoked or the blob leaks for
      // the life of the document.
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
